import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/kfmApi";
import { BookOpen, Compass } from "lucide-react";

const TRANSLATIONS = [
  {
    short: "NIV",
    name: "New International Version",
    tag: "Readable · licensed",
    body: "A clear, modern, thought-for-thought translation that reads naturally. A good everyday starting point for most readers.",
  },
  {
    short: "WEB",
    name: "World English Bible",
    tag: "Modern · public domain",
    body: "A contemporary, freely-available update of the ASV. Easy to read and free of licensing limits — great for sharing.",
  },
  {
    short: "KJV",
    name: "King James Version",
    tag: "Classic · public domain",
    body: "The historic 1611 translation. More formal, poetic language that many know by heart, though some words have shifted meaning over time.",
  },
  {
    short: "ASV",
    name: "American Standard Version",
    tag: "Literal · public domain",
    body: "A very literal 1901 translation prized for word-for-word accuracy. Excellent for careful study of the original wording.",
  },
  {
    short: "NKJV",
    name: "New King James Version",
    tag: "Classic feel · licensed",
    body: "Keeps the dignity and rhythm of the KJV while updating archaic words for today's reader. A comfortable bridge between old and new.",
  },
  {
    short: "AMP",
    name: "Amplified Bible",
    tag: "Expanded · licensed",
    body: "Adds bracketed nuances from the original languages, letting one English verse carry the fuller shades of the Hebrew or Greek word.",
  },
];

const STEPS = [
  ["Come to the text", "Enter a passage (like John 3) or a topic (like anxiety), pick a depth, and press Begin study. No streaks or badges — just you and the Word."],
  ["Read slowly", "The Scripture sits at the top. Tap “I don't understand this” for a plain explanation, or switch the reading level between Simple and Standard. Tap any underlined word — in the text or the word list — to see its sourced Strong's entry, a Word Trail of everywhere it appears, and which authors use it most."],
  ["Explore related passages", "Open “Related passages” under the reading to see other Scriptures that connect — tap one to open it as its own study."],
  ["Go deeper", "Tap “Go Deeper” to talk anything through with the virtual study companion — one-to-one, at your pace. Prefer quiet? Switch to Study Alone anytime."],
  ["Search & revisit", "Use Search to find a word across Scripture or to re-open one of your past studies. Everything you do is saved to your History automatically."],
];

export default function Resources() {
  const navigate = useNavigate();
  const [translations, setTranslations] = useState(TRANSLATIONS);

  useEffect(() => {
    // Prefer the live catalog if available (keeps names in sync with the backend).
    api.get("/config").then(({ data }) => {
      const meta = {};
      TRANSLATIONS.forEach((t) => (meta[t.short] = t));
      const live = (data.translations || [])
        .map((t) => {
          const m = meta[t.short];
          return m ? { ...m, available: t.available } : null;
        })
        .filter(Boolean);
      if (live.length) setTranslations(live);
    }).catch(() => {});
  }, []);

  return (
    <div className="px-6 py-10 space-y-12 md:max-w-3xl md:mx-auto md:py-14 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <header>
        <div className="text-xs font-medium uppercase tracking-[0.25em] text-[#00E5FF] mb-2">Resources</div>
        <h1 className="text-3xl text-white leading-tight">Tools to support your study.</h1>
        <p className="text-sm text-slate-400 mt-2 leading-relaxed">
          A short guide to the translations available in OpenWord AI, and how to get the most out of your time in the Word.
        </p>
      </header>

      {/* How to use */}
      <section className="space-y-4" data-testid="resources-how-to">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-slate-400">
          <Compass className="w-4 h-4 text-[#00E5FF]" strokeWidth={1.5} /> How to use OpenWord AI
        </div>
        <div className="space-y-3">
          {STEPS.map(([h, b], i) => (
            <div key={h} className="flex gap-4 rounded-2xl bg-[#0E131F] border border-[#1E273D] p-5">
              <div className="shrink-0 w-7 h-7 rounded-full bg-[#10222A] text-[#00E5FF] text-sm font-medium flex items-center justify-center">
                {i + 1}
              </div>
              <div>
                <div className="font-medium text-slate-200">{h}</div>
                <p className="text-sm text-slate-400 mt-1 leading-relaxed">{b}</p>
              </div>
            </div>
          ))}
        </div>
        <button
          data-testid="resources-start"
          onClick={() => navigate("/study")}
          className="inline-flex items-center gap-2 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] px-6 py-3 text-sm font-medium transition-colors"
        >
          Start reading
        </button>
      </section>

      {/* Translation guide */}
      <section className="space-y-4" data-testid="resources-translations">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-slate-400">
          <BookOpen className="w-4 h-4 text-[#00E5FF]" strokeWidth={1.5} /> Translation guide
        </div>
        <p className="text-sm text-slate-400 leading-relaxed">
          Every translation is a faithful rendering of the same Scripture — they differ mainly in how closely they follow
          the original word order versus how naturally they read today. Choose your default in Settings.
        </p>
        <div className="space-y-3 md:grid md:grid-cols-2 md:gap-3 md:space-y-0">
          {translations.map((t) => (
            <div key={t.short} className="rounded-2xl bg-[#0E131F] border border-[#1E273D] p-5">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-xl text-white">{t.short}</span>
                <span className="text-[11px] uppercase tracking-widest text-[#00E5FF]">{t.tag}</span>
              </div>
              <div className="text-sm font-medium text-slate-300 mt-0.5">{t.name}</div>
              <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{t.body}</p>
            </div>
          ))}
        </div>
      </section>

      <p className="text-xs text-slate-500 text-center leading-relaxed px-4">
        OpenWord AI is a study tool — not a replacement for a pastor, a church, or the Bible itself.
      </p>
    </div>
  );
}
